import { useEffect, useState } from 'react';
import { Cast, Copy, Maximize, MonitorPlay, Square } from 'lucide-react';
import { Sheet } from '@gmp/shared-ui';
import { party } from '../net/client';
import { QR } from './QR';

/** The parts of the Presentation API we use (not in TypeScript's DOM lib yet). */
interface PresentationConnectionLike extends EventTarget {
  state: 'connecting' | 'connected' | 'closed' | 'terminated';
  terminate(): void;
}
interface PresentationRequestLike {
  start(): Promise<PresentationConnectionLike>;
}
type PresentationRequestCtor = new (urls: string[]) => PresentationRequestLike;

/** True where the browser can cast a page to a Chromecast / Google TV (Chrome on desktop and Android). */
export const canPresent = (): boolean => typeof window !== 'undefined' && 'PresentationRequest' in window && window.isSecureContext !== false;

/** One live cast session for the whole app (the lobby button and the invite sheet share it). */
let connection: PresentationConnectionLike | null = null;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());

function useCasting(): boolean {
  const [, set] = useState(0);
  useEffect(() => {
    const l = () => set((n) => n + 1);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  return !!connection && (connection.state === 'connected' || connection.state === 'connecting');
}

/**
 * Starts casting the audience screen. Resolves to 'started', 'cancelled' (the picker was dismissed or
 * casting refused) or 'unavailable' (no cast device or no Presentation API): the caller then shows the
 * other ways to get the screen onto a TV.
 */
export async function castDisplay(url: string): Promise<'started' | 'cancelled' | 'unavailable'> {
  if (!canPresent()) return 'unavailable';
  try {
    const Ctor = (window as unknown as { PresentationRequest: PresentationRequestCtor }).PresentationRequest;
    const c = await new Ctor([url]).start();
    connection = c;
    const end = () => {
      if (connection === c) connection = null;
      changed();
    };
    c.addEventListener('close', end);
    c.addEventListener('terminate', end);
    c.addEventListener('connect', changed);
    changed();
    return 'started';
  } catch (e) {
    const name = (e as { name?: string }).name;
    // NotAllowedError: the person closed the device picker (or the browser refused); AbortError: same, older Chrome.
    if (name === 'NotAllowedError' || name === 'AbortError') return 'cancelled';
    // NotFoundError: no Chromecast / Google TV found; anything else: casting isn't working here.
    return 'unavailable';
  }
}

/**
 * "Cast to TV": casts the audience screen straight to a Chromecast / Google TV where the browser can,
 * otherwise (iPhone, iPad, Firefox, or no cast device found) opens a sheet with the other ways to get it on
 * a TV. While casting, the same button stops it. `displayLink` is the /display/CODE link (just the party code, so any player may cast it).
 */
export function CastButton({ displayLink, className = 'btn btn-sm btn-secondary', label = 'Cast to TV' }: { displayLink: string; className?: string; label?: string }) {
  const casting = useCasting();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (casting && connection) {
      connection.terminate();
      connection = null;
      changed();
      party.notify('Stopped casting', 'info');
      return;
    }
    if (!canPresent()) {
      setReason(null);
      setOpen(true);
      return;
    }
    setBusy(true);
    const r = await castDisplay(displayLink);
    setBusy(false);
    if (r === 'started') party.notify('Casting the audience screen', 'good');
    else if (r === 'unavailable') {
      setReason('No Chromecast or Google TV answered. Try one of these instead.');
      setOpen(true);
    }
  };
  return (
    <>
      <button className={className} onClick={() => void click()} disabled={busy} data-testid="cast-tv" aria-pressed={casting || undefined}>
        {casting ? <><Square size={16} /> Stop casting</> : <><Cast size={16} /> {label}</>}
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title="Show it on your TV">
        <CastHelp displayLink={displayLink} reason={reason} />
      </Sheet>
    </>
  );
}

/** The other ways to get the audience screen on a TV: the link and QR, then steps per kind of TV. */
export function CastHelp({ displayLink, reason }: { displayLink: string; reason?: string | null }) {
  const copy = () =>
    navigator.clipboard?.writeText(displayLink).then(
      () => party.notify('Audience link copied', 'good'),
      () => party.notify('Could not copy: press and hold the link to copy it', 'bad'),
    );
  return (
    <div className="grid gap-5" data-testid="cast-help">
      {reason && <p className="card p-3 text-sm border-[var(--accent-2)]" role="status">{reason}</p>}
      <div className="flex gap-4 items-center">
        <QR value={displayLink} size={140} />
        <div className="grid gap-2 min-w-0">
          <p className="text-sm text-mute">Open this link on the TV, or on a laptop or tablet connected to it. It shows the game for everyone and never anyone’s secrets.</p>
          <p className="selectable text-xs break-all font-mono bg-ink rounded-xl p-2" data-testid="cast-link">{displayLink}</p>
          <button className="btn btn-sm btn-secondary justify-self-start" onClick={() => void copy()} data-testid="cast-copy"><Copy size={16} /> Copy link</button>
        </div>
      </div>
      <section className="grid gap-1.5">
        <h3 className="font-display font-black text-lg flex items-center gap-2"><MonitorPlay size={20} /> Apple TV</h3>
        <ol className="list-decimal pl-5 text-sm grid gap-1">
          <li>Open the link in Safari on an iPad or Mac.</li>
          <li>Open Control Centre, tap <b>Screen Mirroring</b> and pick your Apple TV.</li>
          <li>On a Mac you can instead use the TV as a separate display and go full screen.</li>
        </ol>
        <p className="text-xs text-mute">Your phone stays your controller, so mirror from another device.</p>
      </section>
      <section className="grid gap-1.5">
        <h3 className="font-display font-black text-lg flex items-center gap-2"><Maximize size={20} /> Smart TV or Fire TV</h3>
        <p className="text-sm">Open the TV’s web browser and type the link, or scan the QR code with a phone that can open it on the TV. Then tap <b>Full screen</b>.</p>
      </section>
      <section className="grid gap-1.5">
        <h3 className="font-display font-black text-lg flex items-center gap-2"><Cast size={20} /> Chromecast or Google TV</h3>
        <p className="text-sm">In Chrome on a laptop, open the link, then use the ⋮ menu → <b>Cast…</b> → <b>Cast tab</b> and pick your TV.</p>
      </section>
    </div>
  );
}

/**
 * One-tap full screen for the audience screen. Hidden where the Fullscreen API isn't available and while
 * the page is already full screen.
 */
export function FullscreenButton({ label = 'Full screen', compact }: { label?: string; compact?: boolean }) {
  const supported = typeof document !== 'undefined' && !!document.fullscreenEnabled && !!document.documentElement.requestFullscreen;
  const [full, setFull] = useState(() => typeof document !== 'undefined' && !!document.fullscreenElement);
  useEffect(() => {
    const on = () => setFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  if (!supported || full) return null;
  return (
    <button
      className="btn btn-sm btn-secondary"
      style={compact ? { minHeight: 0, padding: 7 } : undefined}
      aria-label={label}
      data-testid="tv-fullscreen"
      onClick={() => void document.documentElement.requestFullscreen().catch(() => party.notify('This browser blocked full screen', 'bad'))}
    >
      <Maximize size={compact ? 16 : 18} />{compact ? null : <span>{label}</span>}
    </button>
  );
}

/** Keeps the screen awake while the audience screen is open (re-acquired when the tab comes back). */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let gone = false;
    const get = () => {
      if (document.visibilityState !== 'visible') return;
      navigator.wakeLock.request('screen').then(
        (l) => {
          if (gone) void l.release();
          else lock = l;
        },
        () => undefined,
      );
    };
    get();
    document.addEventListener('visibilitychange', get);
    return () => {
      gone = true;
      document.removeEventListener('visibilitychange', get);
      void lock?.release().catch(() => undefined);
    };
  }, [active]);
}
